"use client";

import React, { FC, useMemo, useState } from "react";
import Link from "next/link";
import { openLiveChat } from "@/components/liveChat/liveChatClient";
import { useParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import { useReviewsAggregate } from "../../(home)/desktop/useReviewsAggregate";
import SectionNewsletter from "../../(home)/desktop/SectionNewsletter";

interface Country {
  code: string;
  name: string;
  slug: string;
  nameTranslated?: string;
  continentId: string;
  mainImage?: string | null;
  numCities?: number;
  numTours?: number;
}

interface Continent {
  code: string;
  name: string;
  slug?: string;
  nameTranslated?: string;
}

interface City {
  id: number;
  name: string;
  slug: string;
  countryId: string;
  nameTranslated?: string;
  mainImage?: string | null;
  tourCount?: number;
  totalTours?: number;
  entry?: { data?: Array<{ title?: string; short_content?: string; mainImage?: string | null }> };
}

export interface CountryPageInitial {
  country: Country | null;
  continent: Continent | null;
  cities: City[];
  cmsTitle: string | null;
  cmsHeroImage: string | null;
  cmsTagline: string | null;
}

interface Props {
  initial: CountryPageInitial;
}

type SortMode = "popularity" | "alpha";

/**
 * Public /country/[slug] — 2026 redesign. Mirrors the /continent layout:
 * full-bleed hero with breadcrumb + CMS-curated tagline, big grid of cities,
 * "why guydeez" + "still unsure?" trust blocks, newsletter.
 *
 * SSR-hydrated from props (see sibling page.tsx). The client component
 * itself only owns interactivity (hover, sort dropdown).
 */
const CountryPage: FC<Props> = ({ initial }) => {
  const { t } = useTranslation("guydeez");
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage
  ) || (lang ? String(lang) : "fr_FR");
  const reviewsAggregate = useReviewsAggregate();

  const country = initial.country;
  const continent = initial.continent;
  const cities = initial.cities;
  const cmsTitle = initial.cmsTitle;
  const cmsHeroImage = initial.cmsHeroImage;
  const cmsTagline = initial.cmsTagline;

  const [sortMode, setSortMode] = useState<SortMode>("popularity");

  const countryLabel =
    capitalize(cmsTitle || country?.nameTranslated || country?.name || "") || "";
  const continentLabel =
    capitalize(continent?.nameTranslated || continent?.name || "") || "";

  const localeFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(selectedLanguage.replace("_", "-"));
    } catch {
      return new Intl.NumberFormat("fr-FR");
    }
  }, [selectedLanguage]);

  const sortedCities = useMemo(() => {
    const list = [...cities];
    if (sortMode === "popularity") {
      return list.sort(
        (a, b) =>
          (b.tourCount || b.totalTours || 0) -
          (a.tourCount || a.totalTours || 0),
      );
    }
    return list.sort((a, b) => {
      const an = (a.nameTranslated || a.name || "").toLowerCase();
      const bn = (b.nameTranslated || b.name || "").toLowerCase();
      return an.localeCompare(bn);
    });
  }, [cities, sortMode]);

  if (!country) {
    return (
      <main
        className="gdx"
        style={{ background: "var(--g-cream)", minHeight: "60vh" }}
      />
    );
  }

  return (
    <>
      <main className="gdx" style={{ background: "var(--g-cream)" }}>
        {/* HERO ------------------------------------------------------- */}
        <section
          style={{
            position: "relative",
            height: "clamp(300px, 35vw, 420px)",
            minHeight: 300,
            backgroundImage: cmsHeroImage
              ? `url("${encodeURI(cmsHeroImage)}")`
              : country.mainImage
                ? `url("${encodeURI(country.mainImage)}")`
                : undefined,
            backgroundColor: "#4a6f5a",
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(20,15,30,.85) 0%, rgba(20,15,30,.55) 60%, rgba(20,15,30,.25) 100%)",
            }}
          />
          <div
            style={{
              position: "relative",
              maxWidth: 1280,
              margin: "0 auto",
              padding: "32px clamp(20px, 4vw, 56px) 48px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Breadcrumb */}
            <nav
              aria-label="Breadcrumb"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                color: "rgba(255,255,255,.75)",
                flexWrap: "wrap",
              }}
            >
              <Link
                href={`/${lang}/${currency}` as any}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {t("breadcrumb_home", { defaultValue: "Accueil" })}
              </Link>
              <span>›</span>
              <Link
                href={`/${lang}/${currency}/continents` as any}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {t("continents_breadcrumb", { defaultValue: "Continents" })}
              </Link>
              {continent?.slug && continentLabel && (
                <>
                  <span>›</span>
                  <Link
                    href={`/${lang}/${currency}/continent/${continent.slug}` as any}
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    {continentLabel}
                  </Link>
                </>
              )}
              <span>›</span>
              <span style={{ color: "white", fontWeight: 600 }}>{countryLabel}</span>
            </nav>

            <div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "5px 12px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,.18)",
                  border: "1px solid rgba(255,255,255,.3)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginBottom: 16,
                }}
              >
                {continentLabel && (
                  <>
                    <span>{continentLabel}</span>
                    <span style={{ opacity: 0.7 }}>·</span>
                  </>
                )}
                <span>{t("country_page_pill", { defaultValue: "Pays" })}</span>
              </span>
              <h1
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: "clamp(40px, 7vw, 64px)",
                  fontWeight: 800,
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  margin: "0 0 16px",
                }}
              >
                {countryLabel}
              </h1>
              {cmsTagline && (
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,.92)",
                    maxWidth: 640,
                    marginBottom: 0,
                  }}
                >
                  {cmsTagline}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Single content container — same pattern as /continent + /continents
            so the entire site shares one horizontal-margin formula. */}
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "0 clamp(20px, 4vw, 56px)",
          }}
        >
          {/* CITIES GRID --------------------------------------------- */}
          <section style={{ padding: "20px 0 24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 20,
                gap: 12,
                flexWrap: "nowrap",
              }}
            >
              <h2
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: "clamp(18px, 4.5vw, 28px)",
                  fontWeight: 800,
                  color: "var(--g-burgundy)",
                  margin: 0,
                  minWidth: 0,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("country_page_cities_title_short", {
                  count: cities.length,
                  defaultValue: `${cities.length} villes`,
                })}
              </h2>
              {cities.length > 1 && (
                <div
                  style={{
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <select
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value as SortMode)}
                    aria-label={t("country_page_sort_label", {
                      defaultValue: "Trier :",
                    })}
                    style={{
                      // Native caret hidden — we render our own ▼ via the
                      // sibling span so the look matches the design pill
                      // (consistent across browsers + Windows + iOS).
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      appearance: "none",
                      padding: "6px 32px 6px 14px",
                      borderRadius: 999,
                      border: "1px solid var(--g-cream-deep)",
                      background: "white",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--g-burgundy)",
                      cursor: "pointer",
                    }}
                  >
                    <option value="popularity">
                      {t("country_page_sort_popularity", { defaultValue: "Popularité" })}
                    </option>
                    <option value="alpha">
                      {t("country_page_sort_alpha", { defaultValue: "A → Z" })}
                    </option>
                  </select>
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 9,
                      color: "var(--g-burgundy)",
                      pointerEvents: "none",
                    }}
                  >
                    ▼
                  </span>
                </div>
              )}
            </div>

            {sortedCities.length === 0 ? (
              <p style={{ color: "var(--g-ink-500)" }}>
                {t("country_page_cities_empty", {
                  defaultValue: "Aucune ville active pour ce pays.",
                })}
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  // Desktop = 4 cols (as before). The min(240, 100%)
                  // wrapper only kicks in below 240 px viewport (very
                  // small phones) so the grid collapses without
                  // overflow there.
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(min(240px, 100%), 1fr))",
                  gap: "clamp(12px, 1.6vw, 18px)",
                }}
              >
                {sortedCities.map((city) => (
                  <CityCard
                    key={city.id}
                    city={city}
                    href={`/${lang}/${currency}/city/${city.slug}`}
                    t={t}
                    localeFormatter={localeFormatter}
                  />
                ))}
              </div>
            )}
          </section>

          {/* WHY GUYDEEZ ---------------------------------------------- */}
          <section style={{ padding: "56px 0 0" }}>
            <div
              style={{
                background: "white",
                borderRadius: 16,
                border: "1px solid var(--g-cream-deep)",
                padding: "40px 32px",
                textAlign: "center",
              }}
            >
              <span className="gdx-eyebrow" style={{ color: "var(--g-coral)" }}>
                {t("country_page_why_eyebrow", {
                  country: countryLabel,
                  defaultValue: `POURQUOI GUYDEEZ EN ${countryLabel.toUpperCase()}`,
                })}
              </span>
              <h2
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: 24,
                  fontWeight: 700,
                  color: "var(--g-burgundy)",
                  margin: "12px auto",
                  maxWidth: 720,
                }}
              >
                {t("country_page_why_title", {
                  country: countryLabel,
                  defaultValue: "Des guides locaux, dans leur propre ville",
                })}
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: "var(--g-ink-500)",
                  maxWidth: 720,
                  margin: "0 auto",
                  lineHeight: 1.55,
                }}
              >
                {t("country_page_why_subtitle", {
                  defaultValue:
                    "Guides locaux triés sur le volet, nés ou installés depuis longtemps dans leur région. Tarif final affiché, billets coupe-file inclus, annulation gratuite jusqu'à 24h avant.",
                })}
              </p>
            </div>
          </section>

          {/* CONTACT REASSURANCE -------------------------------------- */}
          <section style={{ padding: "32px 0 56px" }}>
            <div
              style={{
                background: "var(--g-burgundy)",
                color: "white",
                borderRadius: 16,
                padding: "clamp(24px, 3vw, 32px)",
                display: "grid",
                // Auto-fit lets the CTA wrap below the text on mobile.
                gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
                gap: "clamp(20px, 3vw, 32px)",
                alignItems: "center",
              }}
            >
              <div>
                <span
                  className="gdx-eyebrow"
                  style={{ color: "rgba(255,255,255,.75)" }}
                >
                  {t("country_page_hesitant_eyebrow", {
                    defaultValue: "VOUS HÉSITEZ ?",
                  })}
                </span>
                <h2
                  style={{
                    fontFamily: "var(--g-font-display)",
                    fontSize: 24,
                    fontWeight: 700,
                    marginTop: 8,
                    marginBottom: 8,
                  }}
                >
                  {t("country_page_hesitant_title", {
                    defaultValue:
                      "Discutez avec un conseiller — réponse en quelques minutes",
                  })}
                </h2>
                <p
                  style={{
                    fontSize: 13,
                    color: "rgba(255,255,255,.78)",
                    lineHeight: 1.5,
                  }}
                >
                  {t("country_page_hesitant_subtitle", {
                    defaultValue:
                      "Posez votre question par chat, on vous aide à choisir le bon guide.",
                  })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  void openLiveChat(lang);
                }}
                style={{
                  background: "var(--g-coral)",
                  color: "white",
                  fontWeight: 700,
                  padding: "14px 24px",
                  borderRadius: 999,
                  border: "none",
                  fontSize: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                💬 {t("country_page_hesitant_cta", { defaultValue: "Nous contacter" })}
              </button>
            </div>
          </section>
        </div>
      </main>

      <SectionNewsletter />
    </>
  );
};

const CityCard: FC<{
  city: City;
  href: string;
  t: (key: string, opts?: any) => string;
  localeFormatter: Intl.NumberFormat;
}> = ({ city, href, t, localeFormatter }) => {
  const [hovered, setHovered] = useState(false);
  const cmsTitle = city.entry?.data?.[0]?.title;
  const name = capitalize(
    cmsTitle && cmsTitle !== city.slug
      ? cmsTitle
      : city.nameTranslated || city.name,
  );
  const image =
    city.entry?.data?.[0]?.mainImage || city.mainImage || "";
  const tours = city.tourCount ?? city.totalTours ?? 0;

  return (
    <Link
      href={href as any}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: 14,
        overflow: "hidden",
        background: "white",
        border: "1px solid var(--g-cream-deep)",
        textDecoration: "none",
        color: "var(--g-burgundy)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 16px 28px -16px rgba(20,15,30,0.35)"
          : "0 2px 6px -4px rgba(20,15,30,0.18)",
        transition: "transform 240ms ease, box-shadow 240ms ease",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "4 / 3",
          backgroundImage: image ? `url("${encodeURI(image)}")` : undefined,
          backgroundColor: "#4a6f5a",
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: image ? `url("${encodeURI(image)}")` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: hovered ? "scale(1.05)" : "scale(1)",
            transition: "transform 500ms cubic-bezier(.22,.61,.36,1)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, transparent 40%, rgba(20,15,30,.85) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 14,
            bottom: 12,
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              fontFamily: "var(--g-font-display)",
              lineHeight: 1.1,
            }}
          >
            {name}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "12px 16px",
          fontSize: 12,
          color: "var(--g-ink-500)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>
          {tours > 0
            ? t("country_page_city_tours_count", {
                count: tours,
                defaultValue: `${localeFormatter.format(tours)} tours`,
              })
            : t("country_page_city_tours_none", { defaultValue: "—" })}
        </span>
        <span style={{ color: "var(--g-coral)", fontWeight: 700 }}>
          {t("country_page_city_see", { defaultValue: "Voir" })} →
        </span>
      </div>
    </Link>
  );
};

const capitalize = (s?: string) => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-"),
    )
    .join(" ");
};

export default CountryPage;
